/* eslint-disable max-len */
import React from 'react';
import './BotDetail.scss';
import {
  Container, Col,
  Form, Row, Button,
  Pagination,
  Card,
  InputGroup,
  Badge,
  OverlayTrigger,
  Tooltip
} from 'react-bootstrap';
import BotHistoryTable from '../../components/BotHistoryTable/BotHistoryTable';
import BotHistoryChart from '../../components/BotHistoryChart/BotHistoryChart';
import {
  getBotHistoryRequestAction,
  getCustomBotHistoryRequestAction,
  getRegisteredBotRequestAction,
  setBotHistorySorting,
} from '../../store/bot/botActions';
import { connect } from 'react-redux';
import { useEffect, useState, useMemo } from 'react';
import MultiSignalsBackButton from '../../components/MultiSignalsBackButton/MultiSignalsBackButton';
import { useParams, useLocation } from 'react-router-dom';
import bnbusdt from '../../assets/img/coin/bnbusdt.png';
import okxusdt from '../../assets/img/coin/okxusdt.png';
import * as XLSX from 'xlsx';
import { setLoadingFullAction } from '../../store/loading/loadingActions';

import {
  ChevronDown,
  BarChart3,
  Download,
  Filter,
  Calendar,
  Search,
  Activity,
  TrendingUp,
  TrendingDown,
  Target,
  Clock,
  ChevronLeft,
  ChevronRight,
  MoreHorizontal,
  Info
} from 'lucide-react';

function BotDetail({ ...props }) {
  const {
    botHistoryData,
    botHistorySuccess,
    botHistoryCountData,
    getBotHistoryRequest,
    getBotHistoryRequestAction,
    getCustomBotHistoryRequestAction,
    setLoadingFull,
    registeredBotData,
    getRegisteredBotRequest,
    botHistoryDataPnl,
    botHistoryProfitableTrades,
    botHistoryWinRate,
    setBotHistorySorting,
  } = props;

  const { id } = useParams();
  const location = useLocation();
  const item = location.state?.item;
  const exchanges = location.state?.exchanges;
  const botName = location.state?.name;
  const [dateRange, setDateRange] = useState("1_day");
  const [selectedRange, setSelectedRange] = useState("1_day");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [limit, setLimit] = useState(5);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortDir, setSortDir] = useState("desc");
  const [sortColumn, setSortColumn] = useState("buy_timestamp");
  const [loading, setLoading] = useState(true);
  const [isCustomDateFilter, setIsCustomDateFilter] = useState(false);
  const [pages, setPages] = useState([]);
  const [selectedToken, setSelectedToken] = useState('All');

  const uniqueTokens = useMemo(() => {
    if (!botHistoryData) return [];
    const tokens = botHistoryData.map(item => item.token).filter(Boolean);
    return Array.from(new Set(tokens));
  }, [botHistoryData]);

  const filteredBotHistoryData = useMemo(() => {
    if (!botHistoryData) return [];
    if (selectedToken === 'All') return botHistoryData;
    return botHistoryData.filter(item => item.token === selectedToken);
  }, [botHistoryData, selectedToken]);

  useEffect(() => {
    const bot_id = id;

    if (isCustomDateFilter && startDate && endDate) {
      const payload = {
        bot_id,
        startDate,
        endDate,
        limit_start: (currentPage - 1) * limit,
        limit_end: currentPage * limit,
        sort_dir: sortDir,
        sort_column: sortColumn,
      };

      setLoading(true);
      setLoadingFull({ isLoading: true, loadingText: 'Loading Custom Bot History...' });
      getCustomBotHistoryRequestAction(payload);
    } else {
      const payload = {
        bot_id,
        dateRange,
        limit_start: (currentPage - 1) * limit,
        limit_end: currentPage * limit,
        sort_dir: sortDir,
        sort_column: sortColumn,
      };

      setLoading(true);
      setLoadingFull({ isLoading: true, loadingText: 'Loading Bot History...' });
      getBotHistoryRequestAction(payload);
    }

  }, [dateRange, limit, currentPage, sortDir, sortColumn, id,
    getBotHistoryRequestAction, setLoadingFull, isCustomDateFilter,
    getCustomBotHistoryRequestAction, startDate, endDate]);

  useEffect(() => {
    if (botHistorySuccess) {
      setLoading(false);
      setLoadingFull({ isLoading: false, loadingText: '' });
    }

    const timeoutId = setTimeout(() => {
      setLoading(false);
      setLoadingFull({ isLoading: false, loadingText: '' });
    }, 5000);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [botHistoryData, botHistorySuccess, setLoadingFull]);

  useEffect(() => {
    getBotHistoryRequest();
  }, [getBotHistoryRequest]);

  function handleDateRangeChange(range) {
    setCurrentPage(1);
    setDateRange(range);
    setSelectedRange(range);
    setIsCustomDateFilter(false);
    setStartDate("");
    setEndDate("");
  }

  function handleCustomSearch() {
    if (startDate && endDate) {
      const payload = {
        bot_id: id,
        startDate,
        endDate,
        limit_start: (currentPage - 1) * limit,
        limit_end: currentPage * limit,
        sort_dir: sortDir,
        sort_column: sortColumn,
      };
      setSelectedRange("");
      setCurrentPage(1);
      setLoading(true);
      setLoadingFull({ isLoading: true, loadingText: 'Loading Custom Bot History...' });
      setIsCustomDateFilter(true);

      getCustomBotHistoryRequestAction(payload);
    } else {
      console.error("Both start and end dates are required for custom search.");
    }
  }

  function updateSorting(newSortColumn, newSortDir) {
    setSortColumn(newSortColumn);
    setSortDir(newSortDir);
  }

  const handleLimitChange = (newLimit) => {
    setLimit(newLimit);
    setCurrentPage(1);
  };

  const formatRangeLabel = (range) => {
    return range.replace('_', ' ').replace(/\b\w/g, char => char.toUpperCase());
  };

  const maxRowCountPerPage = limit;
  const maxShowingPage = 5;

  const changePage = (page) => {
    setCurrentPage(page);
    scrollToBotHistoryContainer();
  };

  const scrollToBotHistoryContainer = () => {
    const botHistory = document.getElementById('bot-history-container');
    if (botHistory) {
      botHistory.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const firstPage = () => {
    setCurrentPage(1);
    scrollToBotHistoryContainer();
  };

  const prevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
      scrollToBotHistoryContainer();
    }
  };

  const nextPage = () => {
    if (currentPage < pages.length) {
      setCurrentPage(currentPage + 1);
      scrollToBotHistoryContainer();
    }
  };

  const lastPage = () => {
    setCurrentPage(pages.length);
    scrollToBotHistoryContainer();
  };

  const formatValue = (value) => {
    return value?.toLocaleString('tr-TR');
  };

  useEffect(() => {
    const totalItems = botHistoryCountData?.count;
    const totalPages = Math.ceil(totalItems / limit);
    setPages(Array.from({ length: totalPages }, (_, i) => i + 1));
  }, [limit, botHistoryCountData]);

  const pageRange = pages.slice(
    Math.max(0, currentPage - Math.floor(maxShowingPage / 2) - 1),
    Math.min(pages.length, currentPage + Math.floor(maxShowingPage / 2))
  );

  const exportToExcel = () => {
    const headers = [
      { header: "Order Id", key: "buy_order_id" },
      { header: "Token", key: "token" },
      { header: "Profit Loss $", key: "profit_loss" },
      { header: "Profit Loss %", key: "profit_loss_percentage" },
      { header: "Average Unit Buy Price", key: "avg_buy_price" },
      { header: "Average Unit Sell Price", key: "avg_sell_price" },
      { header: "Total Buy Qty", key: "total_buy_qty" },
      { header: "Total Buy Cost", key: "total_buy_cost" },
      { header: "Total Sell Qty", key: "total_sales_qty" },
      { header: "Total Sell Cost", key: "sales_proceeds" },
      { header: "Buy Timestamp", key: "buy_timestamp" },
      { header: "Sell Timestamp", key: "sales_timestamp" },
      { header: "Status", key: "status" },
    ];

    const formattedData = botHistoryData.map((item) => {
      const formattedItem = {};
      headers.forEach(({ key, header }) => {
        if (key === "profit_loss") {
          formattedItem[header] = item[key] > 0 ? `+${item[key]}` : item[key];
        } else if (key === "status") {
          formattedItem[header] = item[key] ? "Open" : "Closed";
        } else {
          formattedItem[header] = item[key];
        }
      });
      return formattedItem;
    });

    const worksheet = XLSX.utils.json_to_sheet(formattedData, {
      header: headers.map((h) => h.header),
    });

    worksheet["!cols"] = [
      { wpx: 73 }, { wpx: 60 }, { wpx: 67 }, { wpx: 70 }, { wpx: 120 },
      { wpx: 120 }, { wpx: 73 }, { wpx: 77 }, { wpx: 73 }, { wpx: 77 },
      { wpx: 109 }, { wpx: 109 }, { wpx: 39 },
    ];

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "BotHistory");
    XLSX.writeFile(workbook, "BotHistory.xlsx");
  };

  useEffect(() => {
    getRegisteredBotRequest();
  }, [getRegisteredBotRequest]);

  const stats = useMemo(() => {
    if (!filteredBotHistoryData || filteredBotHistoryData.length === 0) return null;
    const totalTrades = filteredBotHistoryData.length;
    const profitableTrades = botHistoryProfitableTrades;
    const totalPnL = botHistoryDataPnl?.pnl || 0;
    const winRate = botHistoryWinRate;

    let buyCommissionTotal = 0;
    let sellCommissionTotal = 0;
    filteredBotHistoryData.forEach((trade) => {
      const buyC = Number(trade?.buy_commission) || 0;
      const sellC = Number(trade?.sell_commission) || 0;
      buyCommissionTotal += buyC;
      sellCommissionTotal += sellC;
    });

    const totalCommission = buyCommissionTotal + sellCommissionTotal;

    return {
      totalTrades,
      profitableTrades,
      totalPnL,
      winRate,
      buyCommissionTotal,
      sellCommissionTotal,
      totalCommission,
    };
  }, [filteredBotHistoryData, botHistoryProfitableTrades, botHistoryDataPnl, botHistoryWinRate]);
  const timeRanges = [
    { key: "1_day", label: "1D", icon: Clock },
    { key: "1_week", label: "1W", icon: Calendar },
    { key: "1_month", label: "1M", icon: Calendar },
    { key: "3_month", label: "3M", icon: Calendar },
    { key: "6_month", label: "6M", icon: Calendar },
    { key: "1_year", label: "1Y", icon: Calendar },
    { key: "All", label: "All", icon: MoreHorizontal }
  ];

  return (
    <div className="modern-bot-detail">
      <Container>
        {/* Back Button */}
        <div className="modern-back-section">
          <MultiSignalsBackButton buttonText="Back" />
        </div>

        {/* Header Section */}
        <div className="bot-detail-header">
          <div className="header-content">
            <div className="header-left">
              <h1 className="page-title">
                <BarChart3 size={32} className="title-icon" />
                Bot Performance Analysis
              </h1>
              <p className="page-subtitle">
                Detailed trading history and performance metrics
              </p>
            </div>
            {registeredBotData && registeredBotData[0]?.exchange && (
              <div className="header-right">
                <div className="exchange-info">
                  <Badge bg="primary" className="exchange-badge">
                    <Activity size={14} className="me-1" />
                    {registeredBotData[0].exchange.charAt(0).toUpperCase() +
                      registeredBotData[0].exchange.slice(1).toLowerCase()}
                    <img
                      src={registeredBotData[0].exchange.toLowerCase() === 'binance' ? bnbusdt : okxusdt}
                      width={20}
                      height={20}
                      className="ms-1"
                      alt="Exchange Logo"
                    />
                  </Badge>
                </div>
              </div>
            )}
          </div>
        </div>
        {/* Filters Section */}
        <div className="filters-section">
          <Row className="g-3 align-items-end">
            {/* Time Range Buttons */}
            <Col lg={8}>
              <div className="time-range-section">
                <label className="filter-section-label">
                  <Clock size={16} className="me-1" />
                  Time Range
                </label>
                <div className="time-range-buttons">
                  {timeRanges.map((range) => (
                    <Button
                      key={range.key}
                      onClick={() => handleDateRangeChange(range.key)}
                      variant={selectedRange === range.key ? "primary" : "outline-primary"}
                      className={`time-range-btn ${selectedRange === range.key ? 'active' : ''}`}
                      size="sm"
                    >
                      <range.icon size={14} className="me-1" />
                      {range.label}
                    </Button>
                  ))}
                </div>
              </div>
            </Col>

            {/* Custom Date Range */}
            <Col lg={4}>
              <div className="custom-date-section">
                <label className="filter-section-label">
                  <Calendar size={16} className="me-1" />
                  Custom Range
                </label>
                <InputGroup className="custom-date-inputs">
                  <Form.Control
                    type="date"
                    value={startDate}
                    onChange={(e) => {
                      const selectedDate = e.target.value;
                      setStartDate(selectedDate);
                      if (endDate && new Date(selectedDate) > new Date(endDate)) {
                        setEndDate("");
                      }
                    }}
                    max={endDate}
                    className="modern-date-input"
                    placeholder="Start Date"
                  />
                  <Form.Control
                    type="date"
                    value={endDate}
                    onChange={(e) => {
                      const selectedDate = e.target.value;
                      setEndDate(selectedDate);
                      if (startDate && new Date(selectedDate) < new Date(startDate)) {
                        setStartDate("");
                      }
                    }}
                    min={startDate}
                    className="modern-date-input"
                    placeholder="End Date"
                  />
                  <Button
                    variant="primary"
                    onClick={handleCustomSearch}
                    disabled={!startDate || !endDate}
                    className="search-btn"
                  >
                    <Search size={16} />
                  </Button>
                </InputGroup>
              </div>
            </Col>
          </Row>
        </div>

        {/* Statistics Cards */}
        {stats && (
          <Row className="g-3 mb-4">
            <Col lg={3} md={6}>
              <Card className="modern-stat-card">
                <Card.Body className="stat-card-body">
                  <div className="stat-icon stat-icon-primary">
                    <Activity size={20} />
                  </div>
                  <div className="stat-content">
                    <div className="stat-value">{botHistoryCountData.count}</div>
                    <div className="stat-label">Total Trades</div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col lg={3} md={6}>
              <Card className="modern-stat-card">
                <Card.Body className="stat-card-body">
                  <div className="stat-icon stat-icon-success">
                    <Target size={20} />
                  </div>
                  <div className="stat-content">
                    <div className="stat-value">{stats.winRate.toFixed(1)}%</div>
                    <div className="stat-label">Win Rate</div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col lg={3} md={6}>
              <Card className="modern-stat-card">
                <Card.Body className="stat-card-body">
                  <div className="stat-icon stat-icon-info">
                    <TrendingUp size={20} />
                  </div>
                  <div className="stat-content">
                    <div className="stat-value">{stats.profitableTrades}</div>
                    <div className="stat-label">Profitable Trades</div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col lg={3} md={6}>
              <Card className="modern-stat-card">
                <Card.Body className="stat-card-body">
                  <div className={`stat-icon ${stats.totalPnL >= 0 ? 'stat-icon-success' : 'stat-icon-danger'}`}>
                    {stats.totalPnL >= 0 ? <TrendingUp size={20} /> : <TrendingDown size={20} />}
                  </div>
                  <div className="stat-content">
                    <div className={`stat-value ${stats.totalPnL >= 0 ? 'positive' : 'negative'}`}>
                      ${stats.totalPnL.toFixed(2)}
                    </div>
                    <div className="stat-label">
                      Total P&L
                      <OverlayTrigger
                        placement="top"
                        overlay={(
                          <Tooltip id="total-pnl-commission-tooltip">
                            <div><strong>Commission breakdown</strong></div>
                            <div>Buy commission total: ${stats.buyCommissionTotal.toFixed(4)}</div>
                            <div>Sell commission total: ${stats.sellCommissionTotal.toFixed(4)}</div>
                            <div><strong>Total commission: ${stats.totalCommission.toFixed(4)}</strong></div>
                          </Tooltip>
                        )}
                      >
                        <span className="info-tooltip-icon" aria-label="Commission details">
                          <Info size={16} />
                        </span>
                      </OverlayTrigger>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        )}

        {/* Chart Section */}
        <Row className="mb-4">
          <Col>
            <Card className="modern-chart-card">
              <Card.Header className="modern-card-header">
                <div className="header-left">
                  <h5 className="card-title">
                    <BarChart3 size={20} className="me-2" />
                    Performance Chart
                  </h5>
                </div>
                <div className="header-right">
                  <div className="token-filter">
                    <Form.Label className="filter-label">
                      <Filter size={16} className="me-1" />
                      Token:
                    </Form.Label>
                    <div className="modern-select-wrapper">
                      <Form.Control
                        as="select"
                        value={selectedToken}
                        onChange={e => setSelectedToken(e.target.value)}
                        className="modern-select"
                      >
                        <option value="All">All Tokens</option>
                        {uniqueTokens.map(token => (
                          <option key={token} value={token}>{token}</option>
                        ))}
                      </Form.Control>
                      <ChevronDown size={16} className="select-arrow" />
                    </div>
                  </div>
                </div>
              </Card.Header>
              <Card.Body className="chart-body">
                <BotHistoryChart
                  botHistoryData={filteredBotHistoryData}
                  selectedToken={selectedToken}
                  allBotHistoryData={botHistoryData}
                />
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* History Table Section */}
        <Row>
          <Col>
            <Card className="modern-history-card" id="bot-history-container">
              <Card.Header className="modern-card-header">
                <div className="header-left">
                  <h5 className="card-title">
                    <Activity size={20} className="me-2" />
                    Trading History
                  </h5>
                  {selectedToken !== 'All' && (
                    <Badge bg="info" className="ms-2">
                      Filtered: {selectedToken}
                    </Badge>
                  )}
                </div>
                <div className="header-right">
                  {botHistoryData?.length > 0 && (
                    <Button
                      variant="outline-primary"
                      className="export-btn"
                      onClick={exportToExcel}
                    >
                      <Download size={16} className="me-1" />
                      Export Excel
                    </Button>
                  )}
                </div>
              </Card.Header>

              <Card.Body className="history-body">


                {/* Table */}
                <div className="table-section">
                  <BotHistoryTable
                    botHistoryData={filteredBotHistoryData}
                    limit={limit}
                    currentPages={currentPage}
                    onLimitChange={handleLimitChange}
                    sortColumn={sortColumn}
                    sortDir={sortDir}
                    onSortChange={updateSorting}
                    loading={loading}
                  />
                </div>

                {/* Pagination */}
                {botHistoryData?.length > 0 && (
                  <div className="pagination-section">
                    <Pagination className="modern-pagination">
                      <Pagination.First
                        onClick={firstPage}
                        disabled={currentPage === 1}
                        className="pagination-control"
                      >
                        <ChevronLeft size={16} />
                        <ChevronLeft size={16} />
                      </Pagination.First>
                      <Pagination.Prev
                        onClick={prevPage}
                        disabled={currentPage === 1}
                        className="pagination-control"
                      >
                        <ChevronLeft size={16} />
                      </Pagination.Prev>

                      {pageRange.map((page, index) => (
                        <Pagination.Item
                          key={index}
                          active={page === currentPage}
                          onClick={() => changePage(page)}
                          className="pagination-item"
                        >
                          {page}
                        </Pagination.Item>
                      ))}

                      <Pagination.Next
                        onClick={nextPage}
                        disabled={currentPage === pages.length}
                        className="pagination-control"
                      >
                        <ChevronRight size={16} />
                      </Pagination.Next>
                      <Pagination.Last
                        onClick={lastPage}
                        disabled={currentPage === pages.length}
                        className="pagination-control"
                      >
                        <ChevronRight size={16} />
                        <ChevronRight size={16} />
                      </Pagination.Last>
                    </Pagination>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
}

const mapStateToProps = (state) => {
  return {
    botHistoryData: state.botReducer.botHistoryData,
    botHistoryDataPnl: state.botReducer.botHistoryDataPnl,
    botHistoryGraphData: state.botReducer.botHistoryGraphData,
    botHistoryWinRate: state.botReducer.botHistoryWinRate,
    botHistoryProfitableTrades: state.botReducer.botHistoryProfitableTrades,
    botHistorySuccess: state.botReducer.botHistorySuccess,
    botHistoryCountData: state.botReducer.botHistoryCountData,
    botStatisticsData: state.botReducer.botStatisticsData,
    isLoading: state.loadingReducer.isLoading,
    user: state.userReducer.user,
    registeredBotData: state.botReducer.registeredBotData,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    getBotHistoryRequest: (payload) => {
      dispatch(getBotHistoryRequestAction(payload));
    },
    getBotHistoryRequestAction: (creds) => {
      dispatch(getBotHistoryRequestAction(creds));
    },
    getCustomBotHistoryRequestAction: (creds) => {
      dispatch(getCustomBotHistoryRequestAction(creds));
    },
    setLoadingFull: (payload) => {
      dispatch(setLoadingFullAction(payload));
    },
    getRegisteredBotRequest: (payload) => {
      dispatch(getRegisteredBotRequestAction(payload));
    },
    setBotHistorySorting: (orderBy, order) => {
      dispatch(setBotHistorySorting(orderBy, order));
    },
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(BotDetail);